import { inject, Injectable } from '@angular/core';
import { PlatformContextService } from '@shared/services/platform-context';

/** Query-string parameter a source adds to its link to the appointment funnel (`?gtm=design`). */
export const TUNNEL_REFERRER_QUERY_PARAM = 'gtm';

/** Parameter values the tagging plan knows, each mapped to the `tunnel_referrer` sent to GA. */
const TUNNEL_REFERRERS = new Map([
  ['design', 'personnalisateur'],
  ['client', 'espace client'],
]);

const TUNNEL_REFERRER_STORAGE_KEY = 'socooc.rdv.tunnelReferrer';

/** CMS page type shared by every appointment-funnel page (steps 1-3 + confirmation). */
const FUNNEL_PAGE_TYPE = 'tunnel';

/**
 * Carries where a visitor entered the appointment funnel from (the configurator, the customer area) to the
 * `tunnel_referrer` of every funnel view, confirmation included. Kept in sessionStorage so it survives the
 * route-based steps and a refresh, and forgotten as soon as the visitor leaves the funnel.
 */
@Injectable({ providedIn: 'root' })
export class TunnelReferrerService {
  private readonly isBrowser = inject(PlatformContextService).isBrowser();

  /** Keeps the referrer a known parameter value names; an unknown value changes nothing. */
  capture(value: string): void {
    const referrer = TUNNEL_REFERRERS.get(value);
    if (this.isBrowser && referrer) {
      sessionStorage.setItem(TUNNEL_REFERRER_STORAGE_KEY, referrer);
    }
  }

  /** The captured referrer on an appointment-funnel page, `null` on any other page or when none was captured. */
  read(pageType: string): string | null {
    if (!this.isBrowser || pageType !== FUNNEL_PAGE_TYPE) {
      return null;
    }
    return sessionStorage.getItem(TUNNEL_REFERRER_STORAGE_KEY);
  }

  /** Forgets the referrer on the first page outside the appointment funnel. */
  clearIfLeftFunnel(pageType: string): void {
    if (this.isBrowser && pageType !== FUNNEL_PAGE_TYPE) {
      sessionStorage.removeItem(TUNNEL_REFERRER_STORAGE_KEY);
    }
  }
}
